import { compileSchema } from '@toolproof-core/validation/_pure/schema-compilation';
import { scenePlanEffectConstraints } from '../_data/scenePlanSchema.js';
import { createScenePlanSchema } from './scenePlanSchema.js';
export function scenePlanCanonicalJson(value) {
    return JSON.stringify(value, (_key, entry) => {
        if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
            return Object.fromEntries(Object.entries(entry).sort(([a], [b]) => a.localeCompare(b)));
        }
        return entry;
    });
}
export function parseScenePlan(value, dialect) {
    const result = compileSchema(createScenePlanSchema(dialect)).validate(value);
    if (!result.valid)
        throw new Error(`Invalid ScenePlan: ${JSON.stringify(result.issues)}`);
    return value;
}
function validatePlanOrder(plan, landmarks, context, dialect) {
    const constraints = [...scenePlanEffectConstraints, ...(dialect.extensions ?? []).map(extension => extension.constraint)];
    const indexOf = (anchor) => {
        const index = landmarks.indexOf(anchor.landmark);
        if (index < 0 || (anchor.phase === 'start' && index === 0))
            throw new Error(`Unknown or initial start anchor '${anchor.landmark}'.`);
        return index - (anchor.phase === 'start' ? 1 : 0);
    };
    const beatIds = new Set();
    let previous = 0;
    for (const beat of plan.beats) {
        if (beatIds.has(beat.id))
            throw new Error(`Duplicate beat '${beat.id}'.`);
        beatIds.add(beat.id);
        const destination = indexOf({ landmark: beat.landmark });
        if (destination < previous)
            throw new Error(`Unordered beat '${beat.id}'.`);
        const opening = beat.replayFrom ? indexOf(beat.replayFrom) : previous;
        if (beat.replayFrom && (destination !== previous || opening >= destination
            || !beat.cues?.length || indexOf(beat.cues[0]) !== opening)) {
            throw new Error(`Replay beat '${beat.id}' must rewind and return to the preceding endpoint.`);
        }
        const cueIds = new Set();
        let actionIndex = opening, textOffset = -1;
        const text = beat.spokenText ?? beat.explanation;
        for (const cue of beat.cues ?? []) {
            const offset = text.indexOf(cue.beforeText);
            const index = indexOf(cue);
            if (cueIds.has(cue.id) || offset < 0 || offset <= textOffset || text.indexOf(cue.beforeText, offset + 1) >= 0
                || index < actionIndex || index > destination)
                throw new Error(`Invalid ordered phrase/action cue '${cue.id}'.`);
            cueIds.add(cue.id);
            textOffset = offset;
            actionIndex = index;
        }
        const segmentIds = new Set();
        let previousCue = -2;
        for (const segment of beat.presentation ?? []) {
            const cueIndex = segment.afterCue === undefined ? -1 : beat.cues?.findIndex(cue => cue.id === segment.afterCue) ?? -1;
            if (segmentIds.has(segment.id) || cueIndex <= previousCue || (segment.afterCue !== undefined && cueIndex < 0)) {
                throw new Error(`Invalid presentation source/order '${segment.id}'.`);
            }
            segmentIds.add(segment.id);
            previousCue = cueIndex;
            const cue = cueIndex < 0 ? undefined : beat.cues?.[cueIndex];
            const start = cue ? indexOf(cue) : opening;
            if (segment.until && (indexOf(segment.until) < start || indexOf(segment.until) > destination)) {
                throw new Error(`Invalid end anchor '${segment.id}'.`);
            }
            const available = start === 0 ? context.initialTargets : context.actions[start - 1]?.targets ?? [];
            for (const rule of constraints) {
                const effects = segment.effects.filter(effect => effect.effectKind === rule.effectKind);
                if (rule.maximumPerSegment !== undefined && effects.length > rule.maximumPerSegment) {
                    throw new Error(`Conflicting '${rule.effectKind}' effects.`);
                }
                if (effects.length && ((rule.stationary && start !== destination) || (rule.beforeCuesOnly && cue))) {
                    throw new Error(`Effect '${rule.effectKind}' requires ${rule.stationary ? 'a stationary beat' : 'activation'}${rule.beforeCuesOnly ? ' without an afterCue' : ''}.`);
                }
                for (const effect of effects)
                    for (const field of rule.targetFields) {
                        const target = Object.getOwnPropertyDescriptor(effect, field)?.value;
                        if (target === undefined || !available.some(availableTarget => scenePlanCanonicalJson(availableTarget) === scenePlanCanonicalJson(target))) {
                            throw new Error(`Target not available at presentation anchor '${segment.id}': ${JSON.stringify(target)}`);
                        }
                    }
            }
        }
        previous = destination;
    }
}
export function validateScenePlanContext(context, dialect) {
    if (!context || context.schemaVersion !== 1 || !Array.isArray(context.actions) || !Array.isArray(context.initialTargets)
        || !context.actions.length || context.actions.length > 128)
        throw new Error('Missing or oversized ScenePlan authoring context.');
    for (const action of context.actions) {
        if (!action || typeof action.landmark !== 'string' || !action.landmark.trim()
            || typeof action.description !== 'string' || !action.description.trim()
            || typeof action.before !== 'string' || !action.before.trim()
            || typeof action.after !== 'string' || !action.after.trim() || !Array.isArray(action.targets)) {
            throw new Error('Authoring actions require descriptions, before/after evidence and targets.');
        }
    }
    const landmarks = ['initial', ...context.actions.map(action => action.landmark)];
    if (new Set(landmarks).size !== landmarks.length)
        throw new Error('Authoring action landmarks must be unique.');
    const schema = createScenePlanSchema(dialect);
    const validateTarget = compileSchema(schema).fragment('#/$defs/target');
    if (!validateTarget)
        throw new Error('ScenePlan target schema is unavailable.');
    for (const target of [...context.initialTargets, ...context.actions.flatMap(action => action.targets)]) {
        const result = validateTarget.validate(target);
        if (!result.valid)
            throw new Error(`Invalid authoring target: ${JSON.stringify(result.issues)}`);
    }
    validateScenePlan(context.approvedPlan, context, dialect);
}
export function validateScenePlan(value, context, dialect) {
    // Schema first; ordering uses the actual replay, not just beat destinations.
    const plan = parseScenePlan(value, dialect);
    if (scenePlanCanonicalJson(plan.trace) !== scenePlanCanonicalJson(context.approvedPlan.trace))
        throw new Error('Generated plan changed its source trace.');
    validatePlanOrder(plan, ['initial', ...context.actions.map(action => action.landmark)], context, dialect);
    if (plan.beats[0]?.landmark !== 'initial' || plan.beats.at(-1)?.landmark !== context.actions.at(-1)?.landmark) {
        throw new Error('Plan must cover the initial and final replay states.');
    }
    return plan;
}
export function validateScenePlanGenerationInput(input) {
    validateScenePlanContext(input?.context, input?.dialect);
    if (!input.guidance || Object.keys(input.guidance).some(key => key !== 'teachingNotes')
        || !Array.isArray(input.guidance.teachingNotes) || input.guidance.teachingNotes.length > 32
        || input.guidance.teachingNotes.some(note => typeof note !== 'string' || !note.trim() || note.length > 2000)
        || input.guidance.teachingNotes.join('').length > 16000) {
        throw new Error('Generation guidance requires at most 32 nonempty teaching notes (2000 characters each, 16000 total).');
    }
    if (input.instructions !== undefined && (typeof input.instructions !== 'string' || input.instructions.length > 4000)) {
        throw new Error('Authoring instructions must be text of at most 4000 characters.');
    }
    if (input.modelName !== undefined && (typeof input.modelName !== 'string' || !/^[a-zA-Z0-9._-]{1,100}$/.test(input.modelName))) {
        throw new Error('Invalid scene-plan model name.');
    }
}
